import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import PanelCard, { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import FilterTabs from '../../components/ui/FilterTabs';
import Pagination from '../../components/ui/Pagination';
import StatCard, { STAT_ICONS } from '../../components/ui/StatCard';

const WINDOWS = [
  { id: 'all', label: 'All time' },
  { id: '30d', label: 'Last 30 days' },
  { id: '7d',  label: 'Last 7 days' },
];

const PAGE_SIZE = 20;

/** `2026-09-12T06:49:00.000Z` → `2026-09-12 06:49` (UTC, ahogy a szerver küldi). */
const stamp = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

const euro = (n) => `€${Number(n || 0).toFixed(2)}`;

/**
 * Miért nem lett jóváírás — a szerver csak azt állítja, amit a tények bírnak el.
 *
 * Az `unknown` szándékosan nem találgat: a jóváírás pillanatában érvényes
 * előfizetés-állapotot senki nem őrzi meg, tehát utólag nem bizonyítható. A
 * címke ezt mondja ki, nem azt, hogy „biztosan lejárt az előfizetése".
 */
const REASONS = {
  not_paid: {
    label: 'Order not paid',
    tone: 'bg-white/[0.05] text-gray-400 border-white/[0.10]',
    title: 'The code was attached to the order, but the order never reached delivered state.',
  },
  plan_too_short: {
    label: 'Plan too short',
    tone: 'bg-amber-500/12 text-amber-300 border-amber-500/25',
    title: 'Delivered, but the plan is not longer than the reward, so no days are granted.',
  },
  unknown: {
    label: 'No reward at delivery',
    tone: 'bg-sky-500/12 text-sky-300 border-sky-500/25',
    title: 'Delivered, but nothing was credited — most likely the code owner had no active subscription at that moment. That state is not recorded, so it cannot be proven afterwards.',
  },
};

/**
 * Ajánlói áttekintés a stábnak.
 *
 * Három szint egy oldalon, mert három különböző kérdés van:
 *   - a fejléc számai: mennyibe kerül és mennyit hoz a rendszer
 *   - a top lista:     kinek a kódja megy
 *   - a tételes lista: ki mikor kinek a kódját váltotta be
 *
 * Az IDŐABLAK a fenti kettőre hat, a KERESÉS csak a tételes listára. Ez
 * szándékos: egy névre keresve a fejléc számai félrevezetőek lennének, mintha az
 * egész rendszer annyit termelt volna.
 */
export default function ReferralsTab() {
  const [windowKey, setWindowKey] = useState('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');       // a késleltetett keresőszó
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Gépelés közben ne menjen kérés minden leütésre.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ window: windowKey, page, limit: PAGE_SIZE });
      if (query) params.set('search', query);
      const res = await api.get(`/admin/referrals?${params}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [windowKey, page, query]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary;
  const top = data?.topReferrers ?? [];
  const rows = data?.redemptions ?? [];
  const total = data?.total ?? 0;
  const rewardDays = data?.rewardDays ?? 7;
  const notCredited = data?.notCredited;

  if (loading && !data) {
    return <div className="py-12 flex justify-center"><LoadingSpinner label="Loading referrals…" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Ha a rendszer ki van kapcsolva, a számok múltbeliek — ezt ki kell mondani,
          különben úgy tűnik, hogy egyszerűen nincs forgalom. */}
      {data && !data.enabled && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg text-sm
                        bg-amber-500/10 border border-amber-500/25 text-amber-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 mt-0.5">
            <path d="M12 9v4M12 17h.01" />
            <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
          </svg>
          <span className="leading-relaxed">
            The referral system is turned off right now (Owner Panel → Referral system),
            so no new codes can be redeemed. Everything below is history.
          </span>
        </div>
      )}

      <PanelCard
        title="Referrals"
        subtitle={
          `Each redemption credits the code owner with +${rewardDays} days, ` +
          `and only counts on plans longer than ${rewardDays} days. Times are UTC.`
        }
        actions={<FilterTabs filters={WINDOWS} current={windowKey}
                             onChange={w => { setWindowKey(w); setPage(1); }} />}
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Redemptions" value={summary?.redemptions ?? 0}
                    icon={STAT_ICONS.sparkle} tone="brand" />
          {/* A kiadott napok a rendszer ÁRA — ezért van a bevétel mellett. */}
          <StatCard label="Days granted" value={`+${summary?.daysGranted ?? 0}`}
                    icon={STAT_ICONS.check} tone="success" />
          <StatCard label="Referrers" value={summary?.referrers ?? 0}
                    icon={STAT_ICONS.users} />
          <StatCard label="Order value" value={euro(summary?.revenue)}
                    icon={STAT_ICONS.money} tone="brand" />
        </div>
      </PanelCard>

      <PanelCard
        flush
        title="Top referrers"
        /* Ha többen vannak, mint amennyi kifér, azt KI KELL MONDANI — különben a
           lista úgy néz ki, mintha ennyi ajánló lenne összesen. */
        subtitle={top.length === 0
          ? 'Nobody has brought a paid order in this period.'
          : (summary && summary.referrers > top.length
              ? `Top ${top.length} of ${summary.referrers} accounts with at least one redemption in this period.`
              : `${top.length} account(s) with at least one redemption in this period.`)}
      >
        {top.length === 0 ? (
          <EmptyState
            icon={<><path d="M17 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9.5" cy="7" r="4" /></>}
            title="No referrals in this period."
            hint="Pick a wider period, or check that the referral system is on."
          />
        ) : (
          <div className="scroll-x">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                <tr>
                  <th className="text-left px-5 py-3 table-head">Account</th>
                  <th className="text-left px-5 py-3 table-head">Redemptions</th>
                  <th className="text-left px-5 py-3 table-head">Days earned</th>
                  <th className="text-left px-5 py-3 table-head hidden sm:table-cell">Order value</th>
                  <th className="text-left px-5 py-3 table-head hidden md:table-cell">Last one</th>
                </tr>
              </thead>
              <tbody>
                {top.map(t => (
                  <tr key={t.id} className="table-row">
                    <td className="px-5 py-3">
                      <span className="font-medium text-white">{t.username}</span>
                      {t.isBanned && (
                        <span className="ml-2 badge bg-red-500/12 text-red-300 border-red-500/25">banned</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-300 tabular-nums">{t.redemptions}</td>
                    <td className="px-5 py-3 text-emerald-300 font-semibold tabular-nums">
                      +{t.daysGranted}
                    </td>
                    <td className="px-5 py-3 text-gray-400 tabular-nums hidden sm:table-cell">
                      {euro(t.revenue)}
                    </td>
                    <td className="px-5 py-3 text-gray-500 font-mono text-xs hidden md:table-cell">
                      {stamp(t.lastAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PanelCard>

      <PanelCard
        flush
        title="Redemptions"
        subtitle="Who used whose code, and what it paid out. The search filters this list only."
      >
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <div className="relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="w-4 h-4 text-gray-600 absolute left-3.5 top-1/2 -translate-y-1/2">
              <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
            </svg>
            <input
              type="text"
              className="input pl-10"
              placeholder="Search by username — either side of the referral…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" /></>}
            title={query ? 'Nothing matches this search.' : 'No redemptions in this period.'}
            hint={query ? 'The name has to match one of the two accounts.' : undefined}
          />
        ) : (
          <>
            {/* Mobilon kártyák: a hat oszlop 375 px-en oldalra görgetett. */}
            <div className="md:hidden divide-y divide-white/[0.05]">
              {rows.map(r => (
                <div key={r.id} className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-200">{r.referred_username}</span>
                    <span className="text-gray-600">→</span>
                    <span className="text-brand-200">{r.referrer_username}</span>
                    <span className="ml-auto text-emerald-300 font-semibold tabular-nums">+{r.days_granted}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                    <span className="font-mono">{stamp(r.created_at)}</span>
                    <span>{r.plan_label}</span>
                    <span className="tabular-nums">{euro(r.final_price)} · {r.payment_method}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="scroll-x hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                  <tr>
                    <th className="text-left px-5 py-3 table-head">When</th>
                    <th className="text-left px-5 py-3 table-head">Used by</th>
                    <th className="text-left px-5 py-3 table-head">Code owner</th>
                    <th className="text-left px-5 py-3 table-head hidden sm:table-cell">Plan</th>
                    <th className="text-left px-5 py-3 table-head hidden md:table-cell">Order</th>
                    <th className="text-left px-5 py-3 table-head">Granted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id} className="table-row">
                      <td className="px-5 py-3 font-mono text-xs text-gray-400 whitespace-nowrap">
                        {stamp(r.created_at)}
                      </td>
                      <td className="px-5 py-3 text-gray-200">{r.referred_username}</td>
                      <td className="px-5 py-3">
                        <span className="text-brand-200">{r.referrer_username}</span>
                      </td>
                      <td className="px-5 py-3 text-gray-400 hidden sm:table-cell">
                        {r.plan_label}
                      </td>
                      <td className="px-5 py-3 text-gray-500 hidden md:table-cell">
                        <span className="tabular-nums">{euro(r.final_price)}</span>
                        <span className="text-gray-700"> · {r.payment_method}</span>
                      </td>
                      <td className="px-5 py-3 text-emerald-300 font-semibold tabular-nums">
                        +{r.days_granted}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {total > PAGE_SIZE && (
              <div className="px-5 py-4 border-t border-white/[0.06]">
                <Pagination
                  page={page}
                  totalPages={Math.ceil(total / PAGE_SIZE)}
                  total={total}
                  label="redemptions"
                  onPage={setPage}
                />
              </div>
            )}
          </>
        )}
      </PanelCard>

      {/* A fenti lista csak a KIFIZETETT jutalmakat tartalmazza. Egy kód viszont
          már a rendelés felvételekor rákerül a rendelésre — ami utána lejárhat,
          vagy kézbesítésnél kieshet. Ezek sehol máshol nem látszanak, pedig épp
          ezekre kérdeznek rá („az én kódommal vett, mégsem kaptam semmit"). */}
      {notCredited && notCredited.total > 0 && (
        <PanelCard
          flush
          title="Used a code — nothing credited"
          subtitle={
            notCredited.total > notCredited.rows.length
              ? `${notCredited.rows.length} most recent of ${notCredited.total} order(s) in this period. Filtered by order date; the search above does not apply here.`
              : `${notCredited.total} order(s) in this period. Filtered by order date; the search above does not apply here.`
          }
        >
          {/* Mobilon kártyák: a hat oszlop 375 px-en oldalra görgetett. */}
          <div className="md:hidden divide-y divide-white/[0.05]">
            {notCredited.rows.map(r => {
              const reason = REASONS[r.reason] || REASONS.unknown;
              return (
                <div key={r.order_id} className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-200">{r.referred_username}</span>
                    <span className="text-gray-600">→</span>
                    <span className="text-brand-200">{r.referrer_username}</span>
                    <span title={reason.title} className={'ml-auto badge border ' + reason.tone}>{reason.label}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                    <span className="font-mono">{stamp(r.created_at)}</span>
                    <span>{r.plan_label || '—'}</span>
                    <span className="tabular-nums">{euro(r.final_price)} · {r.status}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="scroll-x hidden md:block">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                <tr>
                  <th className="text-left px-5 py-3 table-head">Order date</th>
                  <th className="text-left px-5 py-3 table-head">Buyer</th>
                  <th className="text-left px-5 py-3 table-head">Code owner</th>
                  <th className="text-left px-5 py-3 table-head hidden sm:table-cell">Plan</th>
                  <th className="text-left px-5 py-3 table-head hidden md:table-cell">Order</th>
                  <th className="text-left px-5 py-3 table-head">Why not</th>
                </tr>
              </thead>
              <tbody>
                {notCredited.rows.map(r => {
                  const reason = REASONS[r.reason] || REASONS.unknown;
                  return (
                    <tr key={r.order_id} className="table-row">
                      <td className="px-5 py-3 font-mono text-xs text-gray-400 whitespace-nowrap">
                        {stamp(r.created_at)}
                      </td>
                      <td className="px-5 py-3 text-gray-200">{r.referred_username}</td>
                      <td className="px-5 py-3 text-brand-200">{r.referrer_username}</td>
                      <td className="px-5 py-3 text-gray-400 hidden sm:table-cell">
                        {r.plan_label || '—'}
                      </td>
                      <td className="px-5 py-3 text-gray-500 hidden md:table-cell">
                        <span className="tabular-nums">{euro(r.final_price)}</span>
                        <span className="text-gray-700"> · {r.status}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span title={reason.title}
                              className={`badge border ${reason.tone}`}>
                          {reason.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </PanelCard>
      )}
    </div>
  );
}
